import { Tag, TagLabel, TextProps, Tooltip } from "@chakra-ui/react";
import * as CSS from "csstype";
import { Feature, Geometry } from "geojson";

import { useFileUpload } from "../context";
import { SceneFeature, ModelRun } from "../types/scenes";
import { UploadStatus } from "../types/uploads";
import { CollecticonCircleInformation } from "@devseed-ui/collecticons-chakra";

const statusColors = {
  pending: "purple",
  failed: "red",
  processing: "secondary",
  succeeded: "green",
};

export function ModelStatusTag({ run, fontProps }: { run: ModelRun, fontProps?: TextProps }) {
  if(!run.status.length) return null; 
  
  return (
    <Tag
      borderRadius="full"
      size="sm"
      colorScheme={statusColors[run.status]}
      display="inline-flex"
      alignItems="center"
      {...fontProps}
    >
      Model {run.status}
      {Number(run.status_reason?.length) > 0 && (
        <Tooltip label={run.status_reason}>
          <CollecticonCircleInformation size="3" ml="2" />
        </Tooltip>
      )}
    </Tag>
  );
}

type Props = {
  scene: Feature<Geometry, SceneFeature>;
};

function SceneStatusTag({ scene }: Props) {
  const { is_valid, validation_error, model_runs } = scene.properties;
  const { status: uploadStatus = {} } = useFileUpload();
  const sceneUploadStatus = uploadStatus[scene?.properties.s3_prefix];
  const hasUploadsInProgress = sceneUploadStatus?.multiSpectral === UploadStatus.Uploading || sceneUploadStatus?.panchromatic === UploadStatus.Uploading;

  const fontProps = {
    size: "sm",
    borderRadius: "full",
    fontSize: "xs",
    fontFamily: "mono",
    whiteSpace: "pre",
    letterSpacing: "0.25px",
    textTransform: "uppercase" as CSS.Property.TextTransform,
  };

  if (hasUploadsInProgress || (!is_valid && !validation_error)) {
    return (
      <Tag {...fontProps} colorScheme="primary">
        Uploads pending
      </Tag>
    );
  }

  if (!is_valid && validation_error) {
    return (
      <Tag {...fontProps} colorScheme="red">
        <TagLabel>Uploads invalid</TagLabel>
        <Tooltip label={validation_error}>
          <CollecticonCircleInformation size="3" ml="2" />
        </Tooltip>
      </Tag>
    );
  }

  const orderedModelRuns = model_runs.sort(
    (a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()
  );

  const latestModelRun = orderedModelRuns[0];
  if (!model_runs.length) return null;
  const hasSuccessModelRuns = model_runs.some(({ status }) => status === "succeeded");
  if (hasSuccessModelRuns) return null;
  if (latestModelRun.status !== "succeeded") {
    return (
      <ModelStatusTag fontProps={fontProps} run={latestModelRun} />
    );
  }

  return null;
}

export default SceneStatusTag;
